import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useRef,
  useCallback,
} from "react";
import {
  createClient,
  User as SupabaseUser,
  Session,
  SupabaseClient,
} from "@supabase/supabase-js";

const supabaseUrl =
  process.env.REACT_APP_SUPABASE_URL || process.env.NEXT_PUBLIC_SUPABASE_URL;
const supabaseAnonKey =
  process.env.REACT_APP_SUPABASE_ANON_KEY ||
  process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    "Supabase URL and Anon Key must be set in environment variables."
  );
}
const supabase = createClient(supabaseUrl, supabaseAnonKey);

interface User extends SupabaseUser {
  name?: string;
  role?: string;
}

interface SubAccountUser {
  id: string;
  username: string;
  displayName: string;
  studioId: string;
  permissions: string[];
  sessionToken: string;
  refreshToken?: string;
  isSubAccount: true;
}

interface AuthContextType {
  session: Session | null;
  user: User | SubAccountUser | null;
  loading: boolean;
  supabase: SupabaseClient;
  setSession: (session: Session | null) => void;
  setUser: (user: User | SubAccountUser | null) => void;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  subAccountLogin: (
    username: string,
    password: string,
    studioCode: string
  ) => Promise<void>;
  isSubAccount: boolean;
  hasPermission: (permission: string) => boolean;
  getAuthToken: () => string | null;
  refreshSubAccountToken: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Clear all cookies function
const clearAllCookies = () => {
  const cookies = document.cookie.split(";");
  for (let cookie of cookies) {
    const eqPos = cookie.indexOf("=");
    const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${window.location.hostname}`;
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
  }
};

// Helper function to decode JWT and check expiration
const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
  } catch {
    return true; // If we can't decode it, consider it expired
  }
};

// Helper function to check if token expires within X minutes
const isTokenExpiringSoon = (
  token: string,
  minutesFromNow: number = 5
): boolean => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    const expirationBuffer = minutesFromNow * 60; // Convert to seconds
    return payload.exp < currentTime + expirationBuffer;
  } catch {
    return true;
  }
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | SubAccountUser | null>(null);
  const [loading, setLoading] = useState(true);
  const refreshing = useRef(false);
  const refreshInterval = useRef<NodeJS.Timeout | null>(null);

  // Function to refresh sub-account token
  const refreshSubAccountToken = async (): Promise<boolean> => {
    if (refreshing.current) {
      return false; // Prevent multiple simultaneous refresh attempts
    }

    if (
      !user ||
      !("isSubAccount" in user) ||
      !user.isSubAccount ||
      !user.refreshToken
    ) {
      return false;
    }

    refreshing.current = true;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/sub-account/refresh`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ refreshToken: user.refreshToken }),
        }
      );

      if (!response.ok) {
        throw new Error("Token refresh failed");
      }

      const data = await response.json();
      if (data.success && data.sessionToken) {
        // Update user with new session token
        const updatedUser: SubAccountUser = {
          ...user,
          sessionToken: data.sessionToken,
        };

        setUser(updatedUser);
        sessionStorage.setItem(
          "subAccountSession",
          JSON.stringify(updatedUser)
        );
        return true;
      }

      return false;
    } catch (error) {
      // If refresh fails, logout the user
      await logout();
      return false;
    } finally {
      refreshing.current = false;
    }
  };

  // Set up automatic token refresh
  useEffect(() => {
    if (
      user &&
      "isSubAccount" in user &&
      user.isSubAccount &&
      user.sessionToken
    ) {
      // Clear any existing interval
      if (refreshInterval.current) {
        clearInterval(refreshInterval.current);
      }

      // Check token expiration every minute
      refreshInterval.current = setInterval(async () => {
        if (isTokenExpiringSoon(user.sessionToken, 5)) {
          await refreshSubAccountToken();
        }
      }, 60000); // Check every minute

      return () => {
        if (refreshInterval.current) {
          clearInterval(refreshInterval.current);
        }
      };
    }
  }, [user]);

  useEffect(() => {
    let mounted = true;

    // Check for sub-account session first - use sessionStorage for better security
    const subAccountSession = sessionStorage.getItem("subAccountSession");
    if (subAccountSession) {
      try {
        const subAccountUser = JSON.parse(subAccountSession);

        // Validate the parsed session data
        if (
          subAccountUser &&
          typeof subAccountUser.id === "string" &&
          typeof subAccountUser.sessionToken === "string" &&
          subAccountUser.sessionToken.length >= 10 &&
          subAccountUser.isSubAccount === true
        ) {
          // Check if token is expired
          if (isTokenExpired(subAccountUser.sessionToken)) {
            // Stored sub-account token is expired, removing session
            sessionStorage.removeItem("subAccountSession");
          } else {
            if (mounted) {
              setUser(subAccountUser);
              setLoading(false);
            }
            return;
          }
        } else {
          // Invalid sub-account session data
          sessionStorage.removeItem("subAccountSession");
        }
      } catch (error) {
        // Error parsing sub-account session
        sessionStorage.removeItem("subAccountSession");
      }
    }

    // Get initial session for main accounts
    supabase.auth.getSession().then(({ data }) => {
      if (mounted) {
        setSession(data.session);
        setUser(data.session?.user ?? null);
        setLoading(false);
      }
    });

    // Listen for auth changes for main accounts
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (mounted) {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
      if (refreshInterval.current) {
        clearInterval(refreshInterval.current);
      }
    };
  }, []);

  // Logout function
  const logout = async () => {
    try {
      setLoading(true);

      // Clear refresh interval
      if (refreshInterval.current) {
        clearInterval(refreshInterval.current);
      }

      // Check if this is a sub-account logout
      if (user && "isSubAccount" in user && user.isSubAccount) {
        const subUser = user as SubAccountUser;
        // Call sub-account logout endpoint to revoke session
        try {
          await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/supabase/sub-account/logout`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${subUser.sessionToken}`,
                "Content-Type": "application/json",
              },
            }
          );
        } catch (error) {
          // Error revoking sub-account session - continue with logout
        }
      } else {
        // Sign out from Supabase for main accounts
        await supabase.auth.signOut();
      }

      // Clear state
      setSession(null);
      setUser(null);

      // Remove sub-account session
      localStorage.removeItem("subAccountSession"); // Clean up old localStorage data
      sessionStorage.removeItem("subAccountSession"); // Remove current sessionStorage data

      // Remove all supabase/local auth keys
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith("sb-") || key.includes("supabase"))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
      sessionStorage.clear();

      // Clear all cookies securely
      clearAllCookies();

      // Force redirect to login page to prevent session restoration
      window.location.href = "/login";
    } catch (e) {
      // Even if there's an error, force logout
      localStorage.removeItem("subAccountSession");
      sessionStorage.clear();
      setSession(null);
      setUser(null);
      window.location.href = "/login";
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        throw new Error("Login failed");
      }

      const userData = await response.json();
      setUser(userData);
    } catch (error) {
      throw error;
    }
  };

  const subAccountLogin = async (
    username: string,
    password: string,
    studioCode: string
  ) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/sub-account/login`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ username, password, studioCode }),
        }
      );

      if (!response.ok) {
        throw new Error("Sub-account login failed");
      }

      const data = await response.json();
      if (data.success) {
        const subAccountUser: SubAccountUser = {
          ...data.account,
          isSubAccount: true,
        };
        setUser(subAccountUser);
        // Use sessionStorage instead of localStorage for better security
        sessionStorage.setItem(
          "subAccountSession",
          JSON.stringify(subAccountUser)
        );
      }
    } catch (error) {
      throw error;
    }
  };

  const isSubAccount =
    user && "isSubAccount" in user && user.isSubAccount === true;

  const hasPermission = (permission: string): boolean => {
    if (!user) return false;
    if ("isSubAccount" in user && user.isSubAccount) {
      const subUser = user as SubAccountUser;
      // Messaging and SMS were consolidated: "messaging" covers Email, SMS, SMS History, Push.
      // Treat "sms" as "messaging" so sub-accounts that only had SMS still have access.
      if (permission === "messaging" && subUser.permissions.includes("sms")) {
        return true;
      }
      return subUser.permissions.includes(permission);
    }
    // Main account has all permissions
    return true;
  };

  const getAuthToken = useCallback((): string | null => {
    try {
      if (!user) return null;

      if ("isSubAccount" in user && user.isSubAccount) {
        const subUser = user as SubAccountUser;
        const token = subUser.sessionToken;

        // Validate token format
        if (typeof token !== "string" || token.length < 10) {
          return null;
        }

        // Check if token is expired
        if (isTokenExpired(token)) {
          // Sub-account token is expired, attempting refresh
          // Try to refresh token automatically
          refreshSubAccountToken();
          return null;
        }

        return token;
      }

      // Main account uses Supabase session token
      const token = session?.access_token;

      // Validate main account token format
      if (token && (typeof token !== "string" || token.length < 10)) {
        return null;
      }

      return token || null;
    } catch (error) {
      return null;
    }
  // Intentionally omit refreshSubAccountToken from deps — it changes identity each render;
  // [user, session] is enough for a stable token accessor across ordinary re-renders.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, session]);

  const value = {
    session,
    user,
    loading,
    supabase,
    setSession,
    setUser,
    logout,
    isAuthenticated: !!user && !loading,
    login,
    subAccountLogin,
    isSubAccount: !!isSubAccount,
    hasPermission,
    getAuthToken,
    refreshSubAccountToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};